/**
 * PLC 代码生成器类型定义
 */

// ============================================
// PLC 语言类型
// ============================================

/** 支持的 PLC 语言类型 */
export type PlcLanguageType = 'st' | 'il' | 'lad' | 'fbd';

/** PLC 语言元信息 */
export interface PlcLanguageInfo {
  readonly id: PlcLanguageType;
  readonly name: string;
  readonly description: string;
  readonly fileExtension: string;
  /** 是否为图形化语言（LAD/FBD 需要特殊渲染） */
  readonly isGraphical: boolean;
}

/** 所有支持的 PLC 语言 */
export const PLC_LANGUAGES: readonly PlcLanguageInfo[] = [
  {
    id: 'st',
    name: 'ST',
    description: '结构化文本 (Structured Text)',
    fileExtension: '.st',
    isGraphical: false
  },
  {
    id: 'il',
    name: 'IL',
    description: '指令表 (Instruction List)',
    fileExtension: '.il',
    isGraphical: false
  },
  {
    id: 'lad',
    name: 'LAD',
    description: '梯形图 (Ladder Diagram)',
    fileExtension: '.lad',
    isGraphical: true
  },
  {
    id: 'fbd',
    name: 'FBD',
    description: '功能块图 (Function Block Diagram)',
    fileExtension: '.fbd',
    isGraphical: true
  }
] as const;

// ============================================
// 代码状态
// ============================================

/** 代码编辑状态 */
export interface PlcCodeState {
  /** 当前代码内容 */
  readonly content: string;
  /** 当前语言类型 */
  readonly language: PlcLanguageType;
  /** 是否有未保存的修改 */
  readonly isDirty: boolean;
  /** 最后修改时间 */
  readonly lastModified: string | null;
  /** 代码来源：用户输入 / AI 生成 */
  readonly source: 'user' | 'ai';
}

/** 代码历史记录项 */
export interface PlcCodeHistoryItem {
  readonly id: string;
  readonly content: string;
  readonly language: PlcLanguageType;
  readonly timestamp: string;
  readonly source: 'user' | 'ai';
  /** AI 生成时的提示词 */
  readonly prompt?: string;
}

// ============================================
// AI 生成相关
// ============================================

/** 从 AI 响应中解析出的代码块 */
export interface ParsedCodeBlock {
  readonly language: PlcLanguageType | 'unknown';
  readonly content: string;
  /** 代码块在原始文本中的起始位置 */
  readonly startIndex: number;
  /** 代码块在原始文本中的结束位置 */
  readonly endIndex: number;
}

/** AI 代码生成请求参数 */
export interface PlcCodeGenerationRequest {
  /** 用户输入的需求描述 */
  readonly prompt: string;
  /** 目标 PLC 语言 */
  readonly targetLanguage: PlcLanguageType;
  /** 当前编辑器中的代码（用于优化/修改场景） */
  readonly currentCode?: string;
  /** 额外上下文信息 */
  readonly context?: Record<string, unknown>;
}

/** AI 代码生成结果 */
export interface PlcCodeGenerationResult {
  /** 生成的代码 */
  readonly code: string;
  /** 代码语言 */
  readonly language: PlcLanguageType;
  /** AI 的解释说明 */
  readonly explanation?: string;
  /** 是否成功 */
  readonly success: boolean;
  /** 错误信息 */
  readonly error?: string;
}

// ============================================
// 编辑器配置
// ============================================

/** 编辑器主题 */
export type EditorTheme = 'vs' | 'vs-dark' | 'hc-black';

/** 编辑器配置 */
export interface PlcEditorConfig {
  readonly theme: EditorTheme;
  readonly fontSize: number;
  readonly tabSize: number;
  readonly wordWrap: 'on' | 'off' | 'wordWrapColumn';
  readonly lineNumbers: 'on' | 'off' | 'relative';
  readonly minimap: boolean;
  readonly readOnly: boolean;
}

/** 默认编辑器配置 */
export const DEFAULT_EDITOR_CONFIG: PlcEditorConfig = {
  theme: 'vs-dark',
  fontSize: 14,
  tabSize: 4,
  wordWrap: 'on',
  lineNumbers: 'on',
  minimap: true,
  readOnly: false
} as const;

// ============================================
// 组件 Props
// ============================================

/** 代码同步事件 */
export interface CodeSyncEvent {
  readonly type: 'code-from-ai' | 'code-to-chat' | 'code-updated';
  readonly code: string;
  readonly language: PlcLanguageType;
  readonly timestamp: string;
}

/** 代码同步回调 */
export interface CodeSyncCallbacks {
  /** 当 AI 生成代码时调用，将代码同步到编辑器 */
  readonly onCodeFromAI?: (code: string, language: PlcLanguageType) => void;
  /** 当用户修改代码时调用 */
  readonly onCodeChanged?: (code: string) => void;
  /** 当需要将代码发送给 AI 时调用 */
  readonly onSendCodeToAI?: (code: string) => void;
}

/** PlcCodeEditor 组件 Props */
export interface PlcCodeEditorProps {
  /** 代码内容（受控） */
  readonly value: string;
  /** 代码变更回调 */
  readonly onChange: (value: string) => void;
  /** 当前语言 */
  readonly language: PlcLanguageType;
  /** 语言变更回调 */
  readonly onLanguageChange?: (language: PlcLanguageType) => void;
  /** 编辑器配置 */
  readonly config?: Partial<PlcEditorConfig>;
  /** 是否显示工具栏 */
  readonly showToolbar?: boolean;
  /** 自定义类名 */
  readonly className?: string;
  /** 是否正在加载（AI 生成中） */
  readonly loading?: boolean;
}

/** AIPlcCoder 主组件 Props */
export interface AIPlcCoderProps {
  /** 业务类型（透传给 AiChatPage） */
  readonly businessType?: string;
  /** 业务 ID（透传给 AiChatPage） */
  readonly businessId?: string | number | null;
  readonly defaultCapabilityCode?: string; // 传递默认能力 code 给 AiChatPage，优先匹配该 code 的能力
  /** 初始代码 */
  readonly initialCode?: string;
  /** 初始语言 */
  readonly initialLanguage?: PlcLanguageType;
  /** 代码变更回调 */
  readonly onCodeChange?: (code: string, language: PlcLanguageType) => void;
  /** 自定义类名 */
  readonly className?: string;
}

// ============================================
// 代码选区相关
// ============================================

/** 编辑器中选中的代码信息 */
export interface CodeSelection {
  /** 选中的代码文本 */
  readonly code: string;
  /** 起始行号（从 1 开始） */
  readonly startLine: number;
  /** 结束行号（从 1 开始） */
  readonly endLine: number;
  /** 选中的语言 */
  readonly language: PlcLanguageType;
  /** 是否为全选 */
  readonly isFullSelection: boolean;
}

/** PlcCodeEditor 选区变更回调 */
export type OnSelectionChangeCallback = (selection: CodeSelection | null) => void;

// ============================================
// Diff 审查相关
// ============================================

/** Diff 审查状态 */
export type DiffReviewStatus = 'idle' | 'reviewing' | 'accepted' | 'rejected';

/** Diff 审查数据 */
export interface DiffReviewState {
  /** 审查状态 */
  readonly status: DiffReviewStatus;
  /** 原始代码（编辑器当前内容） */
  readonly originalCode: string;
  /** AI 生成的新代码 */
  readonly incomingCode: string;
  /** 新代码的语言 */
  readonly incomingLanguage: PlcLanguageType;
  /** 审查开始时间 */
  readonly startedAt: string | null;
}

/** Diff 审查初始状态 */
export const DIFF_REVIEW_INITIAL_STATE: DiffReviewState = {
  status: 'idle',
  originalCode: '',
  incomingCode: '',
  incomingLanguage: 'st',
  startedAt: null
} as const;